import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMPTY_FORM = { title: '', description: '', questions: [], locations: [] };

export default function ManageForms() {
  const [forms, setForms] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newQuestion, setNewQuestion] = useState('');

  const load = async () => {
    try {
      const [formsRes, locRes] = await Promise.all([
        api.get('/tenant/forms'),
        api.get('/locations'),
      ]);
      setForms(formsRes.data.data);
      setAllLocations(locRes.data.data.filter(l => l.isActive));
    } catch { setError('Failed to load forms'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    setForm({ ...form, questions: [...form.questions, { text: newQuestion.trim() }] });
    setNewQuestion('');
  };

  const removeQuestion = (i) => {
    setForm({ ...form, questions: form.questions.filter((_, idx) => idx !== i) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.questions.length === 0) { setError('Add at least one question'); return; }
    try {
      if (editId) {
        await api.put(`/tenant/forms/${editId}`, form);
        setSuccess('Form updated');
      } else {
        await api.post('/tenant/forms', form);
        setSuccess('Form created');
      }
      setForm(EMPTY_FORM); setEditId(null); setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving form');
    }
  };

  const handleEdit = (f) => {
    setForm({ title: f.title, description: f.description || '', questions: f.questions.map(q => ({ text: q.text })), locations: (f.locations || []).map(l => l._id || l) });
    setEditId(f._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this form?')) return;
    try { await api.delete(`/tenant/forms/${id}`); setSuccess('Deleted'); load(); }
    catch { setError('Delete failed'); }
  };

  const handleToggle = async (f) => {
    try {
      await api.put(`/tenant/forms/${f._id}`, { ...f, isActive: !f.isActive, questions: f.questions, locations: (f.locations || []).map(l => l._id || l) });
      load();
    } catch { setError('Update failed'); }
  };

  const EMOJIS = ['😠', '😢', '😐', '😊', '🤩'];
  const LABELS = ['Angry', 'Sad', 'Neutral', 'Happy', 'Excited'];

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
        <div>
          <h3>Feedback Forms</h3>
          <p style={{color: 'var(--text-muted)', fontSize: 14}}>
            Create emoji surveys for in-person and shareable feedback. Audio feedback works without a form.<br/>
            {/*Assign to specific branches or make them available everywhere.*/}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => {
          setShowForm(!showForm);
          setEditId(null);
          setForm(EMPTY_FORM);
        }}>
          {showForm ? 'Cancel' : '+ Create Form'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="card mb-3">
          <h4 style={{ marginBottom: 16 }}>{editId ? 'Edit Form' : 'Create Feedback Form'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Form Title</label>
              <input className="form-control" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Customer Experience Survey" />
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <input className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." />
            </div>

            <div className="form-group">
              <label>Locations <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-muted)' }}>(leave empty = all locations)</span></label>
              {allLocations.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No locations set up yet — go to the Locations tab to add them.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {allLocations.map(loc => {
                    const selected = form.locations.includes(loc._id);
                    return (
                      <button
                        key={loc._id}
                        type="button"
                        onClick={() => {
                          const updated = selected
                            ? form.locations.filter(id => id !== loc._id)
                            : [...form.locations, loc._id];
                          setForm({ ...form, locations: updated });
                        }}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                          border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
                          background: selected ? 'var(--primary-light)' : 'var(--surface)',
                          color: selected ? 'var(--primary)' : 'var(--text-muted)',
                        }}
                      >
                        📍 {loc.name}{loc.city ? ` · ${loc.city}` : ''}
                      </button>
                    );
                  })}
                </div>
              )}
              {form.locations.length === 0 && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>✅ No selection = form visible to all locations</p>
              )}
            </div>
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Each question will be answered with emojis: {EMOJIS.map((e, i) => <span key={i} title={LABELS[i]}>{e} </span>)}
                </p>
                {form.questions.map((q, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ background: 'var(--primary)', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    <span style={{ flex: 1, fontSize: 14 }}>{q.text}</span>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => removeQuestion(i)}>✕</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    className="form-control"
                    value={newQuestion}
                    onChange={e => setNewQuestion(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addQuestion())}
                    placeholder="Type a question and press Add or Enter"
                  />
                  <button type="button" className="btn btn-outline" onClick={addQuestion}>Add</button>
                </div>
              </div>
            <button className="btn btn-primary" type="submit">{editId ? 'Save Changes' : 'Create Form'}</button>
          </form>
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {forms.length === 0 && (
            <div className="card text-center" style={{ padding: 48, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <p>No feedback forms yet. Create your first one!</p>
            </div>
          )}
          {forms.map(f => (
            <div key={f._id} className="card form-card" style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <strong style={{ fontSize: 16 }}>{f.title}</strong>
                  <span className={`badge ${f.isActive ? 'badge-success' : 'badge-gray'}`}>{f.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                {f.description && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>{f.description}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                    {(f.locations || []).length === 0
                      ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>📍 All locations</span>
                      : f.locations.map(loc => (
                          <span key={loc._id || loc} className="badge badge-info" style={{ fontSize: 11 }}>
                            📍 {loc.name || loc}
                          </span>
                        ))
                    }
                  </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {f.questions.map((q, i) => (
                    <span key={i} style={{ background: 'var(--primary-light)', color: 'var(--primary-dark)', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                      Q{i + 1}: {q.text}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button className="btn btn-outline btn-sm" onClick={() => handleEdit(f)}>Edit</button>
                <button className="btn btn-sm" style={{ background: f.isActive ? '#fef9c3' : '#dcfce7', color: f.isActive ? '#92400e' : '#15803d' }} onClick={() => handleToggle(f)}>
                  {f.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(f._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
